import React, {
  FC,
  MutableRefObject,
  ReactElement,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { VariableSizeList } from 'react-window';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { useScrollRestoration } from 'services/ScrollRestoration';

interface VirtualizedListProps {
  items: ReactElement[];
  className?: string;
  defaultHeight?: number;
  saveScroll?: boolean;
  initialScrollOffset?: number;
  overscanCount?: number;
  itemKey?: (index: number, data: ReactElement[]) => string;
  defaultRowHeights?: number[];
  resetIndex?: number;
}

const VirtualizedList: FC<VirtualizedListProps> = ({
  items,
  className,
  defaultHeight = 200,
  saveScroll = true,
  initialScrollOffset,
  overscanCount = 5,
  itemKey,
  defaultRowHeights = [],
  resetIndex = 0,
}) => {
  const listRef: MutableRefObject<VariableSizeList | undefined> = useRef();
  const scrollable = useScrollRestoration();
  const [rowHeights, setRowHeights] = useState<number[]>(defaultRowHeights);
  const [rendered, setRendered] = useState<number>(0);

  useEffect(() => {
    if (resetIndex > 0 && scrollable.current) {
      scrollable.current.scrollTop = 0;
    }
  }, [resetIndex]);

  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [listRef, rowHeights]);

  function getRowHeight(index: number) {
    return rowHeights[index] || defaultHeight;
  }

  function setItemHeight(index: number, size: number) {
    if (rowHeights[index] !== size) {
      setRowHeights((heights) => {
        heights[index] = size;
        return [...heights];
      });
    }
  }

  let finalClassName = 'virtualized-list';
  if (className) finalClassName += ` ${className}`;

  return (
    <div className={finalClassName}>
      <AutoSizer>
        {({ height, width }: Size) => (
          //@ts-expect-error
          <VariableSizeList
            height={height}
            width={width}
            itemCount={items.length}
            itemSize={getRowHeight}
            ref={listRef}
            outerRef={saveScroll && scrollable}
            initialScrollOffset={initialScrollOffset}
            overscanCount={overscanCount}
            itemKey={itemKey}
            onItemsRendered={() => {
              if (saveScroll) {
                //this is necessary to make sure the scrollable ref is updated
                setRendered(rendered + 1);
              }
            }}
          >
            {({ index, style }: { index: number; style: any }) => (
              <Row index={index} setItemHeight={setItemHeight} style={style}>
                {items[index]}
              </Row>
            )}
          </VariableSizeList>
        )}
      </AutoSizer>
    </div>
  );
};

interface RowProps {
  index: number;
  setItemHeight: (index: number, size: number) => void;
  style: any;
  children: ReactNode;
}

const Row: FC<RowProps> = ({ index, style, setItemHeight, children }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (rowRef.current) {
      setItemHeight(index, rowRef.current.offsetHeight);
    }
  }, [index]);

  return (
    <div style={style}>
      <div ref={rowRef} style={{ height: 'fit-content' }}>
        {children}
      </div>
    </div>
  );
};

export default VirtualizedList;
